import { Mail, ArrowRight, Sparkles, Play } from 'lucide-react';
import { Button } from './ui/button';
import Starfield from './Starfield';
import AnimatedGlobe from './AnimatedGlobe';
import LiveDashboardPreview from './LiveDashboardPreview';
import { GoogleIcon, AppleIcon, GitHubIcon, FacebookIcon } from './BrandIcons';

export default function Hero({ onSignIn }) {
  const startGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/build';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section
      id="top"
      className="relative pt-32 lg:pt-40 pb-24 overflow-hidden text-white hero-deep film-grain"
    >
      {/* Deep starfield */}
      <div className="absolute inset-0">
        <Starfield density={200} />
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 hero-grid opacity-30 pointer-events-none" />

      {/* Aurora blurs */}
      <div className="aurora-glow aurora-a" />
      <div className="aurora-glow aurora-b" />
      <div className="aurora-glow aurora-c" />

      {/* Massive animated globe centered behind headline */}
      <div className="absolute top-24 left-1/2 -translate-x-1/2 opacity-70 pointer-events-none">
        <AnimatedGlobe size={780} />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
        {/* Top badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-xl border border-white/15 mb-8">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-white/90">
            Live &middot; 12,437 apps built this week
          </span>
        </div>

        {/* Mega headline */}
        <h1 className="mx-auto max-w-5xl text-[52px] sm:text-7xl lg:text-[96px] font-black tracking-[-0.045em] leading-[0.92] mega-title-glow">
          <span className="block text-white">Build the</span>
          <span className="block gradient-shine">future in one</span>
          <span className="block text-white">
            <span className="italic font-black text-transparent bg-clip-text bg-gradient-to-br from-cyan-300 via-indigo-300 to-fuchsia-300">
              conversation.
            </span>
          </span>
        </h1>

        <p className="mt-8 mx-auto max-w-2xl text-slate-300 text-lg md:text-xl leading-relaxed">
          The AI dev studio that writes the code, wires the database, generates the
          UI, and ships to a global CDN &mdash; while you sip your coffee.
        </p>

        {/* CTA row */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="conic-border">
            <Button
              data-testid="hero-google-btn"
              onClick={startGoogleAuth}
              className="relative h-14 rounded-2xl bg-slate-950 hover:bg-slate-900 text-white text-[15px] font-bold px-7 flex items-center gap-3"
            >
              <GoogleIcon size={20} />
              Start building free
              <ArrowRight size={18} />
            </Button>
          </div>

          <Button
            data-testid="hero-watch-btn"
            onClick={onSignIn}
            className="h-14 rounded-2xl bg-white/8 hover:bg-white/12 border border-white/20 text-white text-[15px] font-semibold px-7 backdrop-blur"
          >
            <span className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center mr-2">
              <Play size={12} fill="white" className="ml-0.5" />
            </span>
            Watch 90s demo
          </Button>
        </div>

        {/* Micro auth pills */}
        <div className="mt-6 flex items-center justify-center gap-2.5">
          <span className="text-[11px] text-white/40 tracking-wider">Or sign in with</span>
          <button
            data-testid="hero-github-btn"
            onClick={onSignIn}
            className="h-8 w-8 rounded-full bg-white/8 hover:bg-white/15 border border-white/15 flex items-center justify-center transition"
          >
            <GitHubIcon size={14} />
          </button>
          <button
            data-testid="hero-apple-btn"
            onClick={onSignIn}
            className="h-8 w-8 rounded-full bg-white/8 hover:bg-white/15 border border-white/15 flex items-center justify-center transition"
          >
            <AppleIcon size={14} />
          </button>
          <button
            data-testid="hero-facebook-btn"
            onClick={onSignIn}
            className="h-8 w-8 rounded-full bg-white/8 hover:bg-white/15 border border-white/15 flex items-center justify-center transition"
          >
            <FacebookIcon size={16} />
          </button>
          <button
            data-testid="hero-email-btn"
            onClick={onSignIn}
            className="h-8 w-8 rounded-full bg-white/8 hover:bg-white/15 border border-white/15 flex items-center justify-center transition"
          >
            <Mail size={14} />
          </button>
        </div>

        {/* Live dashboard preview — the centerpiece */}
        <div className="mt-16 md:mt-20 relative">
          {/* Massive glow underneath */}
          <div
            className="absolute -inset-x-20 -top-10 -bottom-10 rounded-[40px] pointer-events-none"
            style={{
              background:
                'radial-gradient(60% 50% at 50% 40%, rgba(99,102,241,0.35) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />
          <LiveDashboardPreview />

          {/* Floating stat cards */}
          <div className="hidden xl:flex absolute -left-24 top-1/3 flex-col gap-2 px-4 py-3 rounded-2xl bg-white/8 backdrop-blur-xl border border-white/15 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]">
            <div className="text-[10px] tracking-widest text-cyan-200 uppercase font-bold">
              Avg build
            </div>
            <div className="text-2xl font-black text-white">4 min</div>
          </div>

          <div className="hidden xl:flex absolute -right-24 top-1/4 flex-col gap-2 px-4 py-3 rounded-2xl bg-white/8 backdrop-blur-xl border border-white/15 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]">
            <div className="text-[10px] tracking-widest text-fuchsia-200 uppercase font-bold">
              Models
            </div>
            <div className="text-sm font-black text-white leading-tight">
              Claude 4.5
              <br />
              GPT-5 · Gemini
            </div>
          </div>

          <div className="hidden xl:flex absolute -right-16 bottom-24 items-center gap-3 px-4 py-3 rounded-2xl bg-white/8 backdrop-blur-xl border border-white/15 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white">
              <Sparkles size={18} />
            </div>
            <div>
              <div className="text-[10px] tracking-widest text-emerald-200 uppercase font-bold">
                Uptime
              </div>
              <div className="text-sm font-black text-white">99.99%</div>
            </div>
          </div>
        </div>

        {/* Bottom trust strip */}
        <div className="mt-16 flex flex-wrap items-center justify-center gap-6 text-white/50">
          <span className="text-[11px] tracking-[0.3em] uppercase font-semibold">Powered by</span>
          {['Claude Sonnet 4.5', 'GPT-5', 'Gemini 2.5 Pro', 'Nano Banana', 'Stripe', 'MongoDB'].map((n) => (
            <span
              key={n}
              className="text-[12px] font-semibold text-white/75 hover:text-white transition"
            >
              {n}
            </span>
          ))}
        </div>
      </div>

      {/* Fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#02040a] pointer-events-none" />
    </section>
  );
}
